<script setup>
/**
 * FormControlCoursesDatesBookings
 * Manages the display and interaction for course bookings within a specific course date.
 * Provides functionalities for sorting, selecting multiple bookings, sending various types of emails
 * (arrival forms, equipment sales, review requests), printing documents (arrival sheets, log sheets),
 * and archiving bookings. It also handles payment and notes management for individual bookings.
 */
import {ref, reactive, computed, watch, getCurrentInstance, onMounted, nextTick} from 'vue'
import moment from 'moment'
import Button from '../../common/Button.vue'

const props = defineProps({
    currentData: {type: Object, required: true},
    field: {type: Object, required: true}
})

const {proxy} = getCurrentInstance()
const store = proxy.$store

// Configuration
if (props.field) props.field.replaceFormgroup = true

// --- State ---
const orderBy = ref('id')
const orderDesc = ref(false)
const selectedIds = ref([])
const rows = ref([])

const courseDate = ref(null)
const bookings = ref(null)
const customers = ref(null)
const customerIdsWithConsecutiveBookings = ref(null)

const columns = reactive({
    id: 'ID',
    firstName: 'First name',
    lastName: 'Last name',
    multiple: '',
    accommodation: 'Accommodation',
    tandemFlight: 'Tandem flight',
    trikeFlyingWithWheels: 'Trike',
    price: 'Course price',
    depositAmountPaid: 'Deposit payment',
    finalPaymentAmountPaid: 'Final payment',
    equipmentSalesEmailLastSent: 'Equipment sales',
    notes: 'Notes'
})

// --- Computed ---
const courseDateId = computed(() => props.currentData?.id)
const customerIds = computed(() => bookings.value?.map(o => o.customerId))

const orderedRows = computed(() => {
    if (!rows.value) return []
    const copy = [...rows.value].sort((a, b) => {
        const aVal = a[orderBy.value]
        const bVal = b[orderBy.value]
        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return aVal.localeCompare(bVal)
        }
        return (aVal > bVal) ? 1 : -1
    })
    if (orderDesc.value) copy.reverse()
    return copy
})

const allSelected = computed({
    get() {
        return selectedIds.value.length === rows.value.length && rows.value.length > 0
    },
    set(value) {
        if (value) {
            selectedIds.value = rows.value.map(r => r.id)
        } else {
            selectedIds.value = []
        }
    }
})

// --- Loaders ---
const loadCourseDate = async () => {
    if (!courseDateId.value) {
        courseDate.value = null
        return
    }
    courseDate.value = await store.dispatch('itemData/get', {
        tableName: 'courses_dates',
        id: courseDateId.value
    })
}

const loadBookings = async () => {
    if (!courseDateId.value) {
        bookings.value = null
        return
    }
    const obj = await store.dispatch('request/get', {
        url: 'api/component/courses_bookings',
        params: {courseDateId: courseDateId.value, isArchived: 0}
    })
    bookings.value = obj.data.items
}

const loadCustomers = async () => {
    if (!customerIds.value?.length) {
        customers.value = null
        return
    }
    const obj = await store.dispatch('request/get', {
        url: 'api/component/m_registrants_registrants',
        params: {id: customerIds.value}
    })
    customers.value = obj.data.items
}

const loadConsecutive = async () => {
    if (!courseDate.value || !customerIds.value?.length) {
        customerIdsWithConsecutiveBookings.value = null
        return
    }

    const b1 = await store.dispatch('request/get', {
        url: 'api/component/courses_bookings',
        params: {customerId: customerIds.value}
    })
    const allBookings = b1.data.items
    const courseDateIds = allBookings.map(o => o.courseDateId)

    const start = moment(courseDate.value.endDate).subtract(1, 'days').format('Y-MM-DD')
    const end = moment(courseDate.value.endDate).add(2, 'days').format('Y-MM-DD')

    const d1 = await store.dispatch('request/get', {
        url: 'api/component/courses_dates/id',
        params: {id: courseDateIds, startDate: {gt: start, lt: end}}
    })
    const matchIds = d1.data.values || []

    if (matchIds.length) {
        const filtered = allBookings.filter(o => matchIds.includes(o.courseDateId))
        customerIdsWithConsecutiveBookings.value = filtered.map(o => o.customerId)
    } else {
        customerIdsWithConsecutiveBookings.value = []
    }
}

const rebuildRows = () => {
    const out = []
    bookings.value?.forEach(booking => {
        const row = {
            customerId: booking.customerId,
            id: booking.id,
            firstName: booking.firstName,
            lastName: booking.lastName,
            multiple: '', // This column seems to be for an icon, not data
            accommodation: booking.accommodation,
            tandemFlight: booking.tandemFlight,
            trikeFlyingWithWheels: booking.trikeFlyingWithWheels,
            price: booking.price,
            depositAmountPaid: booking.depositAmountPaid,
            finalPaymentAmountPaid: booking.finalPaymentAmountPaid,
            equipmentSalesEmailLastSent: booking.equipmentSalesEmailLastSent,
            notes: booking.notes
        }
        out.push(row)
    })
    rows.value = out
    // Re-evaluate allSelected after rows change
    allSelected.value = selectedIds.value.length === rows.value.length && rows.value.length > 0
}

// --- Watchers ---
watch(courseDateId, () => {
    loadCourseDate()
    loadBookings()
}, {immediate: true})

watch(customerIds, loadCustomers)
watch([courseDate, customerIds], loadConsecutive)
watch([bookings, customers], rebuildRows)

// --- Methods ---
const getDate = (timestamp) => moment(timestamp * 1000).format('DD MMM YYYY, HH:mm')

const goToBooking = (bookingId) => {
    window.location = `/#/courses_bookings/${bookingId}`
}

const sortColumns = (columnName) => {
    orderDesc.value = orderBy.value === columnName && !orderDesc.value
    orderBy.value = columnName
}

const toggleAllCheckboxes = () => {
    allSelected.value = !allSelected.value
}

const selectCheckbox = (id) => {
    const i = selectedIds.value.indexOf(id)
    if (i > -1) selectedIds.value.splice(i, 1)
    else selectedIds.value.push(id)
}

const showNotes = (row) => {
    store.dispatch('modals/show', {
        componentName: 'ShowNotesModal',
        obj: {row}
    })
}

const saveAmount = (event, id, name) => {
    store.dispatch('itemData/update', {
        tableName: 'courses_bookings',
        id,
        data: {[name]: event.target.value}
    })
}

const sendEmail = (params) => store.dispatch('request/post', {url: 'api/email/send', postData: params})

const sendArrivalFormEmails = () => {
    selectedIds.value.forEach(async (bookingId) => {
        const booking = await store.dispatch('itemData/get', {tableName: 'courses_bookings', id: bookingId})
        const customer = await store.dispatch('itemData/get', {
            tableName: 'm_registrants_registrants',
            id: booking.customerId
        })

        await sendEmail({
            to: customer.email,
            subject: `${customer.firstName}, Please complete your Fly Spain booking. Ref: ${booking.id}.`,
            templateId: 87,
            templateVars: {booking_id: bookingId}
        })
        await store.dispatch('itemData/update', {
            tableName: 'courses_bookings',
            id: bookingId,
            data: {confirmationEmailLastSent: Math.ceil(Date.now() / 1000)}
        })
    })
    store.dispatch('toasts/add', {body: 'Emails sent.'})
}

const sendRequestReviewEmails = () => {
    selectedIds.value.forEach(async (bookingId) => {
        const booking = await store.dispatch('itemData/get', {tableName: 'courses_bookings', id: bookingId})
        const customer = await store.dispatch('itemData/get', {
            tableName: 'm_registrants_registrants',
            id: booking.customerId
        })
        await sendEmail({
            to: customer.email,
            subject: `${customer.firstName} review request.`,
            templateId: 88,
            templateVars: {booking_id: bookingId}
        })
    })
    store.dispatch('toasts/add', {body: 'Emails sent.'})
}

const sendEquipmentSalesEmails = () => {
    selectedIds.value.forEach(async (bookingId) => {
        const booking = await store.dispatch('itemData/get', {tableName: 'courses_bookings', id: bookingId})
        const customer = await store.dispatch('itemData/get', {
            tableName: 'm_registrants_registrants',
            id: booking.customerId
        })
        await sendEmail({
            to: customer.email,
            subject: `Hi ${customer.firstName}, need any paragliding equipment for your trip?`,
            templateId: 89,
            templateVars: {booking_id: bookingId}
        })
        await store.dispatch('itemData/update', {
            tableName: 'courses_bookings',
            id: bookingId,
            data: {equipmentSalesEmailLastSent: Math.ceil(Date.now() / 1000)}
        })
    })
    store.dispatch('toasts/add', {body: 'Emails sent.'})
}

const printAirportTransferSheet = (departures) => {
    const documentTitle = departures ? 'Departures' : 'Pickups'
    printDocument(109, documentTitle, {templateVars: {departures}})
}

const printDocument = (templateId, documentTitle, params = {}) => {
    params.bookingIds = selectedIds.value.length ? selectedIds.value : bookings.value.map(o => o.id)
    store.dispatch('request/get', {url: 'api/controller/document/' + templateId, params})
        .then(o => {
            const html = o.data
            const newWindow = window.open()
            newWindow.document.write('<title>' + documentTitle + '</title>')
            newWindow.document.write(html)
        })
}

const archiveBookings = () => {
    if (confirm('Are you sure?')) {
        selectedIds.value.forEach((bookingId) => {
            store.dispatch('itemData/update', {
                tableName: 'courses_bookings',
                id: bookingId,
                data: {isArchived: 1}
            })
        })
        rows.value = rows.value.filter(r => !selectedIds.value.includes(r.id))
        selectedIds.value = [] // Clear selected IDs after archiving
    }
}
</script>

<template>
    <div>
        <p class="text-center">
            <span class="btn-group">
                <button :disabled="selectedIds.length === 0" class="btn btn-light" type="button"
                        @click="sendArrivalFormEmails">
                    Arrival form email
                </button>
                <button :disabled="selectedIds.length === 0" class="btn btn-light" type="button"
                        @click="sendEquipmentSalesEmails">
                    Equipment sales email
                </button>
                <button :disabled="selectedIds.length === 0" class="btn btn-light" type="button"
                        @click="sendRequestReviewEmails">
                    Request review email
                </button>
            </span>
            <span class="btn-group">
                <button :disabled="selectedIds.length === 0" class="btn btn-light" type="button"
                        @click="printDocument(104, 'Arrival sheet', {})">
                    Arrivals sheet
                </button>
                <button :disabled="selectedIds.length === 0" class="btn btn-light" type="button"
                        @click="printAirportTransferSheet(0)">
                    Pickups
                </button>
                <button :disabled="selectedIds.length === 0" class="btn btn-light" type="button"
                        @click="printAirportTransferSheet(1)">
                    Departures
                </button>
            </span>
            <button :disabled="selectedIds.length === 0" class="btn btn-light" type="button" @click="archiveBookings">
                Archive bookings
            </button>

            <button v-if="courseDate" class="btn btn-light" type="button" @click="printDocument(193, 'Weekly log sheet', {
                templateVars: {
                    pageTitle: courseDate.name,
                    customerNames: bookings ? bookings.map(o => o.firstName + ' ' + o.lastName) : []
                }
            })">
                Weekly log sheet
            </button>
        </p>

        <table class="table">
            <thead>
            <tr>
                <td>
                    <input :checked="allSelected" type="checkbox" @click="toggleAllCheckboxes">
                </td>
                <td v-for="(title, name) in columns" :key="name" @click="sortColumns(name)">
                    <i v-show="orderBy === name" role="button"
                       :class="{ 'bi-chevron-down': orderDesc, 'bi-chevron-up': !orderDesc }"/>
                    {{ title }}
                </td>
            </tr>
            </thead>

            <tbody>
            <tr v-for="row in orderedRows" :key="row.id">
                <td>
                    <input :checked="selectedIds.includes(row.id)" type="checkbox" @click="selectCheckbox(row.id)">
                </td>
                <td>
                    <a href @click.prevent="goToBooking(row.id)">{{ row.id }}</a>
                </td>
                <td>{{ row.firstName }}</td>
                <td>{{ row.lastName }}</td>
                <td>
                    <i v-if="customerIdsWithConsecutiveBookings?.includes(row.customerId)" class="bi bi-check2"></i>
                </td>
                <td>{{ row.accommodation }}</td>
                <td>{{ row.tandemFlight ? '€200 extra' : '' }}</td>
                <td>{{ row.trikeFlyingWithWheels ? '€300 extra' : '' }}</td>
                <td>
                    <input v-model="row.price" class="form-control input-sm" size="5"
                           @input="saveAmount($event, row.id, 'price')">
                </td>
                <td>
                    <input v-model="row.depositAmountPaid" class="form-control input-sm" size="5"
                           @input="saveAmount($event, row.id, 'depositAmountPaid')">
                </td>
                <td>
                    <input v-model="row.finalPaymentAmountPaid" class="form-control input-sm" size="5"
                           @input="saveAmount($event, row.id, 'finalPaymentAmountPaid')">
                </td>
                <td>{{ row.equipmentSalesEmailLastSent ? getDate(row.equipmentSalesEmailLastSent) : null }}</td>
                <td>
                    <Button @click.native="showNotes(row)" :active-value="!!row.notes" class="btn-sm">Edit</Button>
                </td>
            </tr>
            </tbody>
        </table>
    </div>
</template>
